import {
  Box,
  CircularProgress,
  TextField,
  Typography
} from "@material-ui/core";
import { Button } from "components";
import { useFormik } from "formik";
import { usePopup } from "hooks/usePopup";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import productBaseMarginService from "services/productBaseMarginService";
import AutoCompleteProductBaseFormik from "shared/AutoCompleteProductBaseFormik";

import {
  Actions,
  FormContent,
  FormTitle,
  SaveButton,
  StyledDrawer
} from "../styles";

const DEFAULT_MARGIN = 0.95;
const getProductLabel = product =>
  `${product.product_base_id || product.id} -
    ${product.product_base_name || product.name}`;
const getProductId = product => product?.product_base_id || product?.id;

export const MarginDrawer = ({ open, close, onExited, product, onSaved }) => {
  const { addPopup } = usePopup();
  const [margin, setMargin] = useState("");
  const [usesDefaultMargin, setUsesDefaultMargin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const requestRef = useRef(0);
  const formik = useFormik({
    initialValues: { productBase: null },
    enableReinitialize: true,
    onSubmit: () => {}
  });

  const selectedProduct = useMemo(() => {
    if (product) return product;
    return formik.values.productBase;
  }, [product, formik.values.productBase]);

  const selectedProductBaseId = getProductId(selectedProduct);

  const loadMargin = useCallback(
    async productBaseId => {
      const requestId = requestRef.current + 1;
      requestRef.current = requestId;
      const isCurrentRequest = () => requestRef.current === requestId;

      if (!productBaseId) {
        setMargin("");
        setUsesDefaultMargin(true);
        return;
      }

      try {
        setLoading(true);
        const { data } = await productBaseMarginService.get(productBaseId);
        if (!isCurrentRequest()) return;
        setMargin(String(data.margin ?? DEFAULT_MARGIN));
        setUsesDefaultMargin(!!data.uses_default_margin);
      } catch {
        if (!isCurrentRequest()) return;
        addPopup({
          type: "error",
          title: "Erro ao carregar margem do produto."
        });
      } finally {
        if (isCurrentRequest()) setLoading(false);
      }
    },
    [addPopup]
  );

  useEffect(() => {
    if (!open) return;

    if (product) {
      requestRef.current += 1;
      setLoading(false);
      setMargin(
        String(
          product.configured_margin ??
            product.effective_margin ??
            product.margin ??
            DEFAULT_MARGIN
        )
      );
      setUsesDefaultMargin(!!product.uses_default_margin);
      return;
    }

    loadMargin(selectedProductBaseId);
  }, [loadMargin, open, product, selectedProductBaseId]);

  useEffect(() => {
    if (!open) {
      requestRef.current += 1;
    }
  }, [open]);

  const handleExited = () => {
    formik.resetForm();
    setMargin("");
    setUsesDefaultMargin(true);
    setLoading(false);
    onExited?.();
  };

  const handleSubmit = async () => {
    const value = Number(margin);
    if (!selectedProductBaseId) {
      addPopup({ type: "error", title: "Selecione um produto base." });
      return;
    }
    if (!Number.isFinite(value) || value < 0 || value > 1) {
      addPopup({
        type: "error",
        title: "Informe uma margem entre 0 e 1."
      });
      return;
    }

    try {
      setSaving(true);
      await productBaseMarginService.save(selectedProductBaseId, {
        margin: value
      });
      addPopup({ type: "success", title: "Margem salva com sucesso." });
      onSaved();
      close();
    } catch (error) {
      addPopup({
        type: "error",
        title:
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          "Erro ao salvar margem."
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <StyledDrawer
      anchor="right"
      open={open}
      onClose={close}
      SlideProps={{ onExited: handleExited }}
      title="Margem de estoque"
    >
      <FormContent>
        <FormTitle>
          {selectedProduct
            ? getProductLabel(selectedProduct)
            : "Selecione um produto base"}
        </FormTitle>

        {!product && (
          <AutoCompleteProductBaseFormik
            formik={formik}
            name="productBase"
            label="Produto base *"
          />
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" padding="2rem">
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TextField
              fullWidth
              variant="outlined"
              type="number"
              label="Margem"
              value={margin}
              onChange={event => setMargin(event.target.value)}
              inputProps={{ step: "0.01", min: 0, max: 1 }}
              helperText="Use um valor entre 0 e 1. Ex.: 0,95 representa 95%."
            />
            <Typography variant="body2" color="textSecondary">
              {usesDefaultMargin
                ? "Este produto está usando a margem padrão 0,95 até que uma margem específica seja cadastrada."
                : "Este produto possui margem cadastrada para o CD atual."}
            </Typography>
          </>
        )}

        <Actions>
          <Button
            fullWidth
            variant="outlined"
            onClick={close}
            disabled={saving}
          >
            Cancelar
          </Button>
          <SaveButton
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            isLoading={saving}
            disabled={loading || !selectedProductBaseId}
          >
            Salvar
          </SaveButton>
        </Actions>
      </FormContent>
    </StyledDrawer>
  );
};
